import React from "react";
import { Link } from "react-router-dom";
import { Crown, ArrowRight } from "lucide-react";

// ── PlanWelcomeHero ─────────────────────────────────────────────────────
// The "You're in, <Plan>!" post-subscription hero rendered on the Billing
// Success page (`/billing/success?session_id=…`) after Stripe confirms the
// payment. See BillingSuccess in pages/Billing.jsx.
//
// Design tokens (matching the reference mocks pixel-for-pixel):
//   • Background: deep navy (#0d0e1a) with a magenta-to-violet radial
//     glow anchored to the TOP-RIGHT corner. Fades back into navy well
//     before the center. Nothing pale/lavender.
//   • Plan name: pink → violet horizontal gradient (rose-400 → violet-500).
//   • Membership card: rotated COUNTERCLOCKWISE (~-8deg) so the top-right
//     edge lifts up and the left dips down.
//   • Sparkles: two 3-bar clusters — one off the card's upper-left corner,
//     one off its lower-right — bursting outward from those tilted corners.
//
// Dynamic per plan slug:
//   founder / qa_founder → "Founder"       + FOUNDER      member badge
//   pro     / qa_pro     → "Founder Pro"   + FOUNDER/PRO  member badge
// Any other slug → renders null so callers can decide their own fallback.
// ─────────────────────────────────────────────────────────────────────────
const PLAN_LABELS = {
  founder:    { headline: "Founder",     badge: ["FOUNDER"] },
  qa_founder: { headline: "Founder",     badge: ["FOUNDER"] },
  pro:        { headline: "Founder Pro", badge: ["FOUNDER", "PRO"] },
  qa_pro:     { headline: "Founder Pro", badge: ["FOUNDER", "PRO"] },
};

export default function PlanWelcomeHero({ user }) {
  const slug = (user?.package_slug || "").toLowerCase();
  const plan = PLAN_LABELS[slug];
  if (!plan) return null;
  const isPro = plan.badge.length > 1;

  return (
    <section
      data-testid={`plan-welcome-hero-${slug}`}
      className="relative overflow-hidden rounded-3xl bg-[#0d0e1a] px-6 py-10 text-white shadow-[0_20px_60px_-25px_rgba(211,23,135,0.35)] sm:px-10 sm:py-12 lg:px-14 lg:py-14"
    >
      {/* Magenta → violet halo that wraps the RIGHT half of the hero and
          hugs the right + bottom edges of the membership card. Anchor
          moved from the far top-right to ~85% x / 55% y so the glow
          crescents down and around the card. Bottom-left of the hero
          stays dark navy. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(65% 80% at 82% 55%, rgba(219,39,119,0.6) 0%, rgba(147,51,234,0.45) 32%, rgba(76,29,149,0.2) 60%, rgba(13,14,26,0) 82%)",
        }}
      />
      {/* Secondary violet band along the far right so the card's right
          edge always has a soft purple halo behind it. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(28% 55% at 96% 60%, rgba(139,92,246,0.55) 0%, rgba(76,29,149,0.2) 55%, rgba(13,14,26,0) 82%)",
        }}
      />
      {/* Concentrated magenta hotspot behind the top-right of the card. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-4 h-[420px] w-[420px] rounded-full bg-fuchsia-500/40 blur-3xl"
      />

      <div className="relative grid gap-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
        {/* ── Copy ── */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-primary">
            Welcome to CPG Institute
          </p>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
            You're in,
            <br />
            <span className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-violet-500 bg-clip-text text-transparent">
              {plan.headline}!
            </span>
          </h1>
          <p className="mt-4 max-w-xl text-base text-white/85 sm:text-lg">
            Your subscription is active. Let's get you started.
          </p>
          <Link
            to="/course"
            data-testid="plan-welcome-start-course"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(219,39,119,0.65)] transition-transform hover:-translate-y-0.5 hover:bg-primary/90 sm:text-base"
          >
            Start the Course
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* ── Membership card (tilted counterclockwise, hidden on mobile) ── */}
        <div className="relative hidden md:block">
          <div
            data-testid="plan-welcome-badge-card"
            className="relative h-56 w-56 -rotate-[8deg] rounded-[22px] bg-[#faf6f0] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.55)] transition-transform hover:-rotate-[6deg] hover:scale-[1.02] lg:h-64 lg:w-64"
          >
            {/* Spark clusters live INSIDE the tilted card wrapper so they
                inherit the -8deg tilt and move with the card. Both sit
                just off opposite corners in open dark space; every bar
                is fully outside the card body (z-10 above the card). */}
            <SparkCluster style={{ top: -6, left: -14 }} direction="left" />
            <SparkCluster style={{ bottom: -6, right: -14 }} direction="right" />
            <div className="flex h-full flex-col items-center justify-center px-6 py-6">
              <Crown
                className="h-12 w-12 text-primary"
                strokeWidth={1.75}
                fill="none"
              />
              <div className="mt-4 flex flex-col items-center text-center leading-[1.02]">
                {plan.badge.map((word) => (
                  <span
                    key={word}
                    className={
                      isPro
                        ? "font-display text-[26px] font-black tracking-[0.12em] text-slate-900 lg:text-[30px]"
                        : "font-display text-[30px] font-black tracking-[0.16em] text-slate-900 lg:text-[34px]"
                    }
                  >
                    {word}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 font-display text-[13px] font-black tracking-[0.42em] text-slate-900">
                <span className="h-[3px] w-2.5 rounded-full bg-primary" />
                MEMBER
                <span className="h-[3px] w-2.5 rounded-full bg-primary" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Three CSS bars fanning out from a shared origin — no SVG.
 * Each bar is 32×5 with a pill radius. Base fan is three angles (-35°,
 * 0°, +35°) with each bar offset 8px along its own ray so the inner ends
 * don't converge. `direction`:
 *   "right" → fan points RIGHT of the origin (bars extend to the right).
 *             Used for the bottom-right cluster.
 *   "left"  → fan points LEFT of the origin (bars extend to the left).
 *             Used for the top-left cluster.
 *
 * `style` positions the cluster's origin (a single 0×0 point) relative
 * to its parent — the tilted card wrapper — so both clusters inherit the
 * -8deg tilt and stay attached to the card's opposite corners.
 */
function SparkCluster({ direction, style }) {
  // Individual bar rotations. For "right" the fan is (-35, 0, +35);
  // for "left" the whole fan is rotated 180° so bars point left.
  const base = direction === "left" ? 180 : 0;
  const angles = [base - 35, base, base + 35];
  const barColor = "#FF1FA3";
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute"
      style={{ width: 0, height: 0, zIndex: 10, ...style }}
    >
      {angles.map((deg, i) => {
        const rad = (deg * Math.PI) / 180;
        const cx = Math.cos(rad);
        const sy = Math.sin(rad);
        // Place the bar so its LEFT-CENTER lands ~14px along its own ray.
        // With `transformOrigin: left center`, `rotate(Xdeg)` then swings
        // the 32×5 rectangle outward from that pivot. 14px (>8px) keeps
        // the three inner ends visibly separated so the cluster reads
        // as three distinct rays instead of merging into an arrow.
        const offset = 14;
        const left = cx * offset;
        const top = sy * offset - 2.5;
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              width: 32,
              height: 5,
              background: barColor,
              borderRadius: 999,
              transformOrigin: "left center",
              transform: `rotate(${deg}deg)`,
              left: `${left}px`,
              top: `${top}px`,
            }}
          />
        );
      })}
    </div>
  );
}
